import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Flame, Minus, Plus, Star } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { WaButton } from "./WaButton";
import { Reveal, SectionHead } from "./Reveal";
import { useNavigate } from "react-router-dom";
import { MenuItemImage } from "./MenuItemImage";

const DietMark = ({ veg }) => (
  <span
    className={`flex h-3.5 w-3.5 items-center justify-center rounded-[3px] border ${
      veg ? "border-green-700" : "border-brand-red"
    }`}
    title={veg ? "Veg" : "Non-Veg"}
  >
    <span className={`h-1.5 w-1.5 rounded-full ${veg ? "bg-green-700" : "bg-brand-red"}`} />
  </span>
);

const MenuItemCard = ({ item, restaurant, rating, preview }) => {
  const { cart, add, setQty } = useStore();
  const navigate = useNavigate();
  const inCart = cart.find((x) => x.id === item.id);
  const unavailable = !item.available;

  return (
    <div
      data-testid={`menu-item-${item.id}`}
      className={`flex gap-4 rounded-2xl bg-white p-4 ring-1 ring-brand-brown/5 transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-brand-brown/10 sm:p-5 ${unavailable ? "opacity-60" : ""}`}
    >
      <MenuItemImage src={item.image_url} alt={item.name} className="h-24 w-24 rounded-xl sm:h-28 sm:w-28" />
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <h4 className="font-serif text-lg font-semibold leading-snug text-brand-ink">{item.name}</h4>
          <span className="shrink-0 font-serif text-lg font-bold text-brand-red">₹{item.price}</span>
        </div>
        {rating && (
          <span className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-brand-amber" data-testid={`menu-rating-${item.id}`}>
            <Star className="h-3.5 w-3.5 fill-brand-amber" /> {rating.avg}
            <span className="font-medium text-brand-mute">({rating.count})</span>
          </span>
        )}
        <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-brand-mute">{item.description}</p>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 text-[11px] font-bold uppercase tracking-wider">
            <span className={`inline-flex items-center gap-1.5 ${item.veg ? "text-green-700" : "text-brand-red"}`}>
              <DietMark veg={item.veg} /> {item.veg ? "Veg" : "Non-Veg"}
            </span>
            {item.spicy && (
              <span className="inline-flex items-center gap-1 text-brand-red">
                <Flame className="h-3.5 w-3.5" /> Spicy
              </span>
            )}
          </div>
          {unavailable ? (
            <span className="text-xs font-bold uppercase tracking-wider text-brand-mute" data-testid={`menu-unavailable-${item.id}`}>
              Unavailable
            </span>
          ) : inCart ? (
            <div className="flex items-center gap-3 rounded-full bg-brand-beige px-2 py-1.5" data-testid={`menu-stepper-${item.id}`}>
              <button onClick={() => setQty(item.id, inCart.qty - 1)} className="rounded-full bg-white p-1 text-brand-brown shadow-sm" aria-label={`Decrease ${item.name} quantity`}>
                <Minus className="h-3.5 w-3.5" />
              </button>
              <span className="w-4 text-center text-sm font-bold text-brand-ink">{inCart.qty}</span>
              <button onClick={() => setQty(item.id, inCart.qty + 1)} className="rounded-full bg-white p-1 text-brand-brown shadow-sm" aria-label={`Increase ${item.name} quantity`}>
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <button
              data-testid={`menu-add-${item.id}`}
              onClick={() => preview ? navigate("/order") : add(item, restaurant)}
              className="inline-flex items-center gap-1.5 rounded-full bg-brand-red px-5 py-2 text-xs font-bold uppercase tracking-[0.14em] text-brand-cream shadow-md shadow-brand-red/20 transition-transform duration-300 hover:scale-105"
            >
              <Plus className="h-3.5 w-3.5" /> {preview ? "Choose restaurant" : "Add"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const MenuSection = ({ categories: categoryProp, items: itemProp, ratings: ratingProp, restaurant = null }) => {
  const store = useStore();
  const categories = categoryProp || store.categories;
  const items = itemProp || store.items;
  const ratings = ratingProp || store.ratings;
  const preview = !restaurant;
  const [tab, setTab] = useState(null);

  const visibleCategories = useMemo(
    () => categories.filter((c) => items.some((i) => i.category_id === c.id)),
    [categories, items]
  );
  const activeTab = tab && visibleCategories.some((c) => c.id === tab) ? tab : visibleCategories[0]?.id;
  const activeItems = items.filter((i) => i.category_id === activeTab);

  return (
    <section id="menu" data-testid="menu-section" className="bg-brand-cream py-20 sm:py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHead
            overline="Our Menu"
            title={preview ? "Menu Preview" : `${restaurant.location_name} Menu`}
            sub={preview ? "Explore a taste of our menu, then choose a restaurant to see its current dishes, prices and availability." : `Order from ${restaurant.display_name}. Prices and availability shown here are specific to this restaurant.`}
          />
          {preview && <Reveal delay={0.1}>
            <span
              data-testid="menu-sample-badge"
              className="inline-flex items-center rounded-full border border-brand-gold/70 bg-brand-gold/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-brown"
            >
              Choose a restaurant to order
            </span>
          </Reveal>}
        </div>

        <Reveal delay={0.15} className="mt-10">
          <div className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4 pb-2 sm:mx-0 sm:flex-wrap sm:px-0" role="tablist" aria-label="Menu categories">
            {visibleCategories.map((c) => (
              <button
                key={c.id}
                data-testid={`menu-tab-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                role="tab"
                aria-selected={activeTab === c.id}
                onClick={() => setTab(c.id)}
                className={`whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-semibold transition-[background-color,color,box-shadow] duration-300 ${
                  activeTab === c.id
                    ? "bg-brand-red text-brand-cream shadow-lg shadow-brand-red/25"
                    : "bg-white text-brand-brown ring-1 ring-brand-brown/10 hover:ring-brand-gold/70"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </Reveal>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab || "empty"}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6"
          >
            {activeItems.map((item) => (
              <MenuItemCard key={item.id} item={item} restaurant={restaurant} rating={ratings[item.id]} preview={preview} />
            ))}
          </motion.div>
        </AnimatePresence>

        {preview && <Reveal delay={0.1} className="mt-12 text-center">
          <WaButton
            testId="menu-ask-todays-special-button"
            label="Ask About Today's Menu"
            fallbackLabel="Call for Today's Menu"
            msg="Hi Lucky's Kitchen! Could you share today's menu and specials?"
            className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-8 py-4 text-sm font-bold uppercase tracking-[0.14em] text-[#0b3d20] shadow-xl shadow-[#25D366]/25 transition-transform duration-300 hover:scale-[1.05]"
          />
        </Reveal>}
      </div>
    </section>
  );
};
